import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import Loading from "react-fullscreen-loading";

// region Imports - Components
import ButtonTable from "@components/Button/ButtonTable";
import DialogConfirmAction from "@components/Dialog/ConfirmAction";
// endregion Imports - Components
// region Imports - External libraries
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, FormHelperText,
  Grid, MenuItem
} from "@material-ui/core";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Checkbox, Select, TextField } from "unform-material-ui";
import { FormHandles } from "@unform/core";
import { Form } from "@unform/web";
import * as Yup from "yup";
import * as dateFns from "date-fns";
import * as _ from "lodash";
// endregion Imports - External libraries
// region Imports - Hooks
import getValidationErrors from "@hooks/getValidationErrors";
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { DataTableMessages, GlobalMessages, HardwareModuleMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Molecules
import NewDataTable, { DataTableActions, DataTableButtons, DataTableColumns } from "@molecules/NewDataTable";
// endregion Imports - Molecules
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Shared
import { HardwareTypes, HardwareTypesID } from "@shared/constants/hardware-types.enum";
import { HardwareModels } from "@shared/constants/hardware-models.enum";
import { HardwareListItemDto } from "@shared/dtos/hardwares/hardware-list-item.dto";
import { HardwareDetailsDto } from "@shared/dtos/hardwares/hardware-details.dto";
import { HardwareTypeOptionDto } from "@shared/dtos/hardware-types/hardware-type-option.dto";
import { VehicleOptionDto } from "@shared/dtos/vehicles/vehicle-option.dto";
import { SimCardOptionDto } from "@shared/dtos/sim-cards/sim-card-option.dto";
import { DriverOptionDto } from "@shared/dtos/drivers/driver-option.dto";
import { HardwareCreateDto } from "@shared/dtos/hardwares/hardware-create.dto";
import { HardwareUpdateDto } from "@shared/dtos/hardwares/hardware-update.dto";
import { StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
// endregion Imports - Shared
// region Imports - Styles
import { ContainerModalFormHardware } from "./styles";
// endregion Imports - Styles
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils

interface ValidateFields { devId: boolean; }
type ActiveHardwareTypeOption = HardwareTypeOptionDto | null;
type ActiveVehicleState = VehicleOptionDto | null;
type ActiveSimCardOption = SimCardOptionDto | null;
type ActiveDriverOption = DriverOptionDto | null;

const HardwaresList: React.FC = () =>{

  const { addToast } = useToast();
  const { t, i18n } = useTranslation();

  // region States
  const [optionsTypesHardware, setOptionsTypesHardware] = useState<HardwareTypeOptionDto[]>([]);
  const [optionsVehicles, setOptionsVehicles] = useState<VehicleOptionDto[]>([]);
  const [optionsSimCards, setOptionsSimCards] = useState<SimCardOptionDto[]>([]);
  const [optionsDrivers, setOptionsDrivers] = useState<DriverOptionDto[]>([]);

  const [activeOptionTypeHardware, setActiveOptionTypeHardware] = useState<ActiveHardwareTypeOption>(null);
  const [activeOptionVehicle, setActiveOptionVehicle] = useState<ActiveVehicleState>(null);
  const [activeOptionSimCard, setActiveOptionSimCard] = useState<ActiveSimCardOption>(null);
  const [activeOptionDriver, setActiveOptionDriver] = useState<ActiveDriverOption>(null);

  const [openOptionsTypesHardware, setOpenOptionsTypesHardware] = useState(false);
  const [openOptionsVehicles, setOpenOptionsVehicles] = useState(false);
  const [openOptionsSimCards, setOpenOptionsSimCards] = useState(false);
  const [openOptionsDrivers, setOpenOptionsDrivers] = useState(false);

  const loadingOptionsTypesHardware = openOptionsTypesHardware && optionsTypesHardware.length === 0;
  const loadingOptionsVehicles = openOptionsVehicles && optionsVehicles.length === 0;
  const loadingOptionsSimCards = openOptionsSimCards && optionsSimCards.length === 0;
  const loadingOptionsDrivers = openOptionsDrivers && optionsDrivers.length === 0;
  const [loadingHardwareList, setLoadingHardwareList] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingValidateFieldDevId, setLoadingValidateDevId] = useState(false);

  const [hardwares, setHardwares] = useState<HardwareListItemDto[]>([]);
  const [hardwareDetails, setHardwareDetails] = useState<HardwareDetailsDto | null>(null);
  const [hardwareToDelete, setHardwareToDelete] = useState<HardwareListItemDto | null>(null);

  const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
  const [openModalFormHardware, setOpenModalFormHardware] = useState(false);

  const [modalType, setModalType] = useState<"details" | "update" | "register">("register");

  const [table, setTable] = useState<DataTables.Api>({} as DataTables.Api);
  const [tableClickedRow, setTableClickedRow] = useState<JQuery<HTMLTableRowElement>>(
    {} as JQuery<HTMLTableRowElement>
  );

  const [activeCheckbox, setActiveCheckbox] = useState<boolean>(true);
  const [blockCapableCheckbox, setBlockCapableCheckbox] = useState<boolean>(false);
  const [waterControlCapableCheckbox, setWaterControlCapableCheckbox] = useState<boolean>(false);
  const [waterCorrectionFactor, setWaterCorrectionFactor] = useState<number>(1.00);
  const [validateFields, setValidateFields] = useState<ValidateFields>({ devId: false });
  // endregion States
  // region Forms
  const formRef = useRef<FormHandles>(null);

  //* Validations of unique fields

  const validations = {

    validateFieldError: (fieldName: string) => {
      if (formRef.current?.getFieldError(fieldName)?.length) formRef.current?.setFieldError(fieldName, "");
    },
    validateForm: async (formData: HardwareDetailsDto, action: "details" | "register" | "update") => {

      try {

        // Revalidate unique fields (If not validate yet)
        if (!validateFields.devId && formData.dev_id.trim().length) await validations.validateDevId(formData.dev_id);

        // Define the validation types
        const schema = Yup.object().shape({
          type: Yup.string().trim().required(t(HardwareModuleMessages.hardwareFormTypeRequired)),
          brand: Yup.string().trim().required(t(HardwareModuleMessages.hardwareFormBrandRequired)),
          model: Yup.string().trim().required(t(HardwareModuleMessages.hardwareFormModelRequired)),
          dev_id: Yup.string().trim().test("validateDevId", formRef.current?.getFieldError("dev_id"),
            () => !(formRef.current?.getFieldError("dev_id")?.length))
            .required(t(HardwareModuleMessages.hardwareFormDevIdRequired)),
          purchase_date: Yup.string().trim().required(t(HardwareModuleMessages.hardwareFormPurchaseDateRequired)),
          water_correction_factor: Yup.number()
            .min(0.50, t(HardwareModuleMessages.waterCorrectionFactorMinError))
            .max(1.50, t(HardwareModuleMessages.waterCorrectionFactorMaxError))
            .test("decimal", t(HardwareModuleMessages.waterCorrectionFactorDecimalError), (value: number) => {
              if (value === undefined) return true;

              return /^\d+(\.\d{1,2})?$/.test(value.toString());
            })
        });

        // Validate inputs
        await schema.validate(formData, { abortEarly: false });

        // Register or update hardware (According action selected)
        if (action === "register") await createHardware(formData);
        else await updateHardware(formData);

      } catch (error) {
        formRef.current?.setErrors(getValidationErrors(error));
      }
    },
    validateDevId: async (devId: string) => {

      setValidateFields({ ...validateFields, devId: false });

      // Validate dev id just have 3 or more characters in on blur
      if (devId.trim().length >= 3 && devId.trim() !== hardwareDetails?.dev_id) {

        try {

          setLoadingValidateDevId(true);

          // Verify if DEV ID is already registered
          const { data } = await api.get(`hardwares/verify-unique-field/devid/${devId.trim()}`);

          if (data.status === "alert") formRef.current?.setFieldError("dev_id", t(HardwareModuleMessages.hardwareFormDevIdAlreadyExists));
          else formRef.current?.setFieldError("dev_id", "");

          setValidateFields({ ...validateFields, devId: true });

        } catch (error) {
          if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
          else {
            addToast(
              { type: "error", title: error.response.data.backend, description: error.response.data.message }
            );
          }
        } finally {
          setLoadingValidateDevId(false);
        }
      }
    }
  };
  // endregion Forms
  // region Constants

  // REVIEW Change fixed values to database get values
  // Some dropdowns values, we used a fixed values
  const fixedValues = {
    Ibutton: {
      brand: t(GlobalMessages.other),
      model: t(HardwareModuleMessages.model)
    },
    OtherTypes: {
      brands: ["Suntech", "Auto Sender", "Fleet", "Queclink", "Teltonika", "Jmak"],
      models: [
        HardwareModels["ST 300HD"],
        HardwareModels["ST 310U"],
        HardwareModels["ST 310UC2"],
        HardwareModels["ST 310U EVT"],
        HardwareModels["ST 340UR"],
        HardwareModels["ST 4315U EVT"],
        HardwareModels["ST 4315U"],
        HardwareModels["ST 4305"],
        HardwareModels["ST 8310U EVT"],
        HardwareModels["ST 8310U"],
        HardwareModels["ST 8300"],
        HardwareModels["2684"],
        HardwareModels["0721"],
        HardwareModels["RAS_1"],
        HardwareModels["GV350MG"],
        HardwareModels["GV350CEU EVT"],
        HardwareModels["GV350CEU"],
        HardwareModels["FMC150 EVT"],
        HardwareModels["FMC150"],
        HardwareModels["FMC650 EVT"],
        HardwareModels["FMC650"],
        HardwareModels["J-R12"],
        HardwareModels["J-R12 EVT"],
        HardwareModels["J-R22"],
        HardwareModels["J-R22 EVT"]
      ] as string[]
    }
  };

  const dataTableSettings: DataTables.Settings = {
    order: [[0, "asc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };
  const dataTableActions: DataTableActions[] = [
    {
      ref: ".modules-hardware-list-details",
      callback: (rowData: HardwareListItemDto) => handleHardwareDetails(rowData)
    },
    {
      ref: ".modules-hardware-list-delete",
      callback: (rowData: HardwareListItemDto) => handleHardwareDelete(rowData)
    }
  ];
  const dataTableColumns: DataTableColumns[] = [
    {
      title: t(HardwareModuleMessages.devId),
      data: "dev_id",
      defaultContent: "",
      filterable: true,
      propertyName: "dev_id"
    },
    {
      title: t(HardwareModuleMessages.type),
      data: "type_description",
      filterable: true,
      propertyName: "type_description"
    },
    {
      title: t(HardwareModuleMessages.brand),
      data: "brand",
      filterable: true,
      propertyName: "brand"
    },
    {
      title: t(HardwareModuleMessages.registrationDate),
      data: "registration_date",
      render: (data) => (data ? utils.formatDateIfHave(new Date(data), "fullDate") : ""),
      filterable: true,
      defaultContent: "",
      propertyName: "registration_date"
    },
    {
      title: t(HardwareModuleMessages.model),
      data: "model",
      filterable: true,
      propertyName: "model"
    },
    {
      title: t(DataTableMessages.status),
      data: (row: HardwareListItemDto) => (
        ReactDOMServer.renderToString(
          <b style={{ color: row.active ? "rgb(70, 193, 125)" : "rgb(230, 74, 25)" }}>{row.active
            ? t(GlobalMessages.active).toUpperCase() : t(GlobalMessages.inactive).toUpperCase()}
          </b>
        )
      ),
      filterable: true,
      propertyName: "active"
    },
    {
      title: t(HardwareModuleMessages.doBlock),
      data: (row: HardwareListItemDto) => (
        ReactDOMServer.renderToString(
          <b style={{ color: row.block_capable ? "rgb(70, 193, 125)" : "rgb(230, 74, 25)" }}>
            {row.block_capable ? t(GlobalMessages.yes).toUpperCase() : t(GlobalMessages.no).toUpperCase()}
          </b>
        )
      ),
      width: "150px",
      filterable: true,
      propertyName: "block_capable"
    },
    {
      title: t(HardwareModuleMessages.doWaterControl),
      data: (row: HardwareListItemDto) => (
        ReactDOMServer.renderToString(
          <b style={{ color: row.water_control_capable ? "rgb(70, 193, 125)" : "rgb(230, 74, 25)" }}>
            {row.water_control_capable ? t(GlobalMessages.yes).toUpperCase() : t(GlobalMessages.no).toUpperCase()}
          </b>
        )
      ),
      width: "150px",
      filterable: true,
      propertyName: "water_control_capable"
    },
    {
      title: t(HardwareModuleMessages.vehicle),
      data: "vehicle_code",
      defaultContent: t(HardwareModuleMessages.hardwareDataTableNoLinkedVehicle),
      filterable: true,
      propertyName: "vehicle_code"
    },
    {
      title: t(HardwareModuleMessages.simCard),
      data: "sim_card_caller_id",
      defaultContent: t(HardwareModuleMessages.hardwareDataTableNoLinkedSimCard),
      filterable: true,
      propertyName: "sim_card_caller_id"
    },
    {
      title: t(HardwareModuleMessages.driver),
      data: "driver_name",
      defaultContent: t(HardwareModuleMessages.hardwareDataTableNoLinkedDriver),
      filterable: true,
      propertyName: "driver_name"
    },
    {
      title: t(DataTableMessages.actions),
      orderable: false,
      searchable: false,
      data: () => ReactDOMServer.renderToString(
        <Grid container spacing={1}>
          <Grid item xs sm md lg xl>
            <ButtonTable className="action-button modules-hardware-list-details">
              <FormatListBulletedIcon />
            </ButtonTable>
          </Grid>
          <Grid item xs sm md lg xl>
            <ButtonTable className="action-button modules-hardware-list-delete">
              <DeleteForeverIcon />
            </ButtonTable>
          </Grid>
        </Grid>
      ),
      width: "130px",
      filterable: false
    }
  ];
  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsAddNew),
      key: "add",
      callback: () => handleHardwareCreate()
    },
    {
      name: t(DataTableMessages.buttonsRefresh),
      key: "refresh",
      callback: () => readHardwares()
    },
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_hardwares_${new Date().toISOString().split("T")[0]}`,
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    }
  ];
  // endregion Constants
  // region Functions

  /**
   * Function to handle the hardware details modal
   *
   * @param hardware Hardware selected to show the details
   */
  const handleHardwareDetails = useCallback(async (hardware: HardwareListItemDto) => {
    setModalType("details");
    setLoadingDetails(true);

    try {
      const { data } = await api.get<StandardReturnObject<HardwareDetailsDto>>(`/hardwares/view/details/${hardware.id_hardware}`);

      if (data.status === "success" && data.result) {
        const details = data.result;

        setHardwareDetails(details);
        setActiveCheckbox(details.active);
        setBlockCapableCheckbox(details.block_capable);
        setWaterControlCapableCheckbox(details.water_control_capable);
        setWaterCorrectionFactor(details.active_water_correction_factor ?? 1.00);

        // region ADAPTER PATTERN & DIRECT ASSIGNMENT

        setActiveOptionTypeHardware(details.type);
        setActiveOptionSimCard(details.sim_card);
        setActiveOptionDriver(details.driver);

        // Vehicle
        if (details.vehicle) {
          setActiveOptionVehicle({
            ...details.vehicle,
            type_description: details.vehicle.type.description
          });
        }

        // endregion ADAPTER PATTERN & DIRECT ASSIGNMENT

        setValidateFields({ devId: true });
        setOpenModalFormHardware(true);
      } else {
        addToast({ type: "error", title: t(ToastMessages.error), description: data.message });
      }
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingDetails(false);
    }
  }, [addToast, t]);

  /**
   * Function to handle the hardware delete modal (Confirm action)
   *
   * @param hardware Hardware selected to delete
   */
  const handleHardwareDelete = useCallback((hardware: HardwareListItemDto) => {
    setHardwareToDelete(hardware);
    setOpenDialogConfirmDelete(true);
  }, []);

  /**
   * Function to handle the hardware create modal (Open form)
   */
  const handleHardwareCreate = () => {
    setModalType("register");
    setOpenModalFormHardware(true);
    setHardwareDetails(null);
  };

  /**
   * Function to handle the hardware form modal close (Reset states)
   */
  const handleCloseModalForm = useCallback(() => {
    setHardwareDetails(null);
    setActiveOptionTypeHardware(null);
    setActiveOptionSimCard(null);
    setActiveOptionVehicle(null);
    setActiveOptionDriver(null);
    setValidateFields({ devId: false });
    setBlockCapableCheckbox(false);
    setWaterControlCapableCheckbox(false);
    setWaterCorrectionFactor(1.00);
  }, []);

  /**
   * Function to read all hardwares
   */
  const readHardwares = useCallback(async () => {
    try {
      setLoadingHardwareList(true);
      const { data } = await api.get<StandardReturnObject<HardwareListItemDto[]>>("hardwares/view/summaries");

      if (data.status === "success" && data.result) {
        setHardwares(data.result);
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        setHardwares([]);
      }
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingHardwareList(false);
    }
  }, [addToast, t]);

  /**
   * Function to create a hardware
   *
   * @param formData Form data to create a hardware
   */
  const createHardware = useCallback(async (formData: HardwareDetailsDto) => {
    try {
      setLoadingHardwareList(true);

      const hardwareToCreate: HardwareCreateDto = {
        type: { id_hardware_type: activeOptionTypeHardware!.id_hardware_type },
        brand: formData.brand,
        model: formData.model,
        dev_id: formData.dev_id,
        purchase_date: new Date(formData.purchase_date),
        active: activeCheckbox,
        block_capable: blockCapableCheckbox,
        water_control_capable: waterControlCapableCheckbox,
        water_correction_factor: waterCorrectionFactor,
        vehicle: activeOptionVehicle ? { id_vehicle: activeOptionVehicle.id_vehicle } : null,
        sim_card: activeOptionSimCard ? { id_sim_card: activeOptionSimCard.id_sim_card } : null,
        driver: activeOptionDriver ? { id_driver: activeOptionDriver.id_driver } : null,
        ibutton_code: formData.type.id_hardware_type === HardwareTypesID.IBUTTON
          ? utils.convertDevIDToIbuttonCode(formData.dev_id)
          : null
      };

      const { data } = await api.post<StandardReturnObject<HardwareDetailsDto>>("hardwares/create", hardwareToCreate);

      if (data.status === "success") {
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
        setOpenModalFormHardware(false);

        await readHardwares();
      } else {
        addToast({ type: "error", title: t(ToastMessages.error), description: data.message });
      }

    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingHardwareList(false);
    }
  }, [
    addToast, t, readHardwares, activeOptionTypeHardware, activeCheckbox,
    blockCapableCheckbox, waterControlCapableCheckbox, waterCorrectionFactor, activeOptionVehicle,
    activeOptionSimCard, activeOptionDriver
  ]);

  /**
   * Function to update a hardware
   *
   * @param formData Form data to update a hardware
   */
  const updateHardware = useCallback(async (formData: HardwareDetailsDto) => {
    if (!hardwareDetails) return;

    try {
      setLoadingHardwareList(true);

      const hardwareToUpdate: HardwareUpdateDto = {
        type: { id_hardware_type: activeOptionTypeHardware!.id_hardware_type },
        brand: formData.brand,
        model: formData.model,
        dev_id: formData.dev_id,
        purchase_date: new Date(formData.purchase_date),
        active: activeCheckbox,
        block_capable: blockCapableCheckbox,
        water_control_capable: waterControlCapableCheckbox,
        water_correction_factor: waterCorrectionFactor,
        vehicle: activeOptionVehicle ? { id_vehicle: activeOptionVehicle.id_vehicle } : { id_vehicle: "" },
        sim_card: activeOptionSimCard ? { id_sim_card: activeOptionSimCard.id_sim_card } : { id_sim_card: "" },
        driver: activeOptionDriver ? { id_driver: activeOptionDriver.id_driver } : { id_driver: "" },
        ibutton_code: formData.type.id_hardware_type === HardwareTypesID.IBUTTON
          ? utils.convertDevIDToIbuttonCode(formData.dev_id)
          : null
      };

      const { data } = await api.patch<StandardReturnObject<boolean>>(`hardwares/update/${hardwareDetails.id_hardware}`, hardwareToUpdate);

      if (data.status === "success") {
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
        setOpenModalFormHardware(false);

        await readHardwares();
      } else {
        addToast({ type: "error", title: t(ToastMessages.error), description: data.message });
      }
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingHardwareList(false);
    }
  }, [
    hardwareDetails, activeOptionTypeHardware, activeCheckbox,
    blockCapableCheckbox, waterControlCapableCheckbox, waterCorrectionFactor, activeOptionVehicle,
    activeOptionSimCard, activeOptionDriver, addToast, t, readHardwares
  ]);

  /**
   * Function to delete a hardware
   */
  const deleteHardware = useCallback(async () => {
    if (!hardwareToDelete) return;

    try {
      setLoadingHardwareList(true);
      const { data } = await api.delete(`hardwares/delete/${hardwareToDelete.id_hardware}`);

      if (data.status === "success") {
        setOpenDialogConfirmDelete(false);
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });

        table.row(tableClickedRow).remove();
        table.draw();

        setHardwareToDelete(null);
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingHardwareList(false);
    }
  }, [hardwareToDelete, table, tableClickedRow, addToast, t]);

  // endregion Functions
  // region Effects

  useEffect(() => {
    readHardwares().then();
  }, [readHardwares, i18n.language]);

  // Type hardware options
  useEffect(() => {
    if (!openOptionsTypesHardware) setOptionsTypesHardware([]);
  }, [openOptionsTypesHardware]);
  useEffect(() => {

    let active = true;

    if (!loadingOptionsTypesHardware) return undefined;

    (async () => {

      try {

        // Get all hardware types
        const { data } = await api.get("hardwares/view/types/options");

        if (data.status === "success") {
          if (active) setOptionsTypesHardware(data.result);
        } else {
          setOpenOptionsTypesHardware(false);
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      } catch (error) {
        setOpenOptionsTypesHardware(false);

        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      }

    })();

    return () => { active = false; };

  }, [loadingOptionsTypesHardware, addToast, t]);

  // Vehicle options
  useEffect(() => {
    if (!openOptionsVehicles) setOptionsVehicles([]);
  }, [openOptionsVehicles]);
  useEffect(() => {

    let active = true;

    if (!loadingOptionsVehicles) return undefined;

    (async () => {

      try {

        // Get all vehicles
        const { data } = await api.get(`vehicles/view/options/${activeOptionTypeHardware?.id_hardware_type}`);

        if (data.status === "success") {
          if (active) setOptionsVehicles(data.result);
        } else {
          setOpenOptionsVehicles(false);
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }

      } catch (error) {

        setOpenOptionsVehicles(false);

        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      }

    })();

    return () => { active = false; };

  }, [activeOptionTypeHardware, loadingOptionsVehicles, addToast, t]);

  // Sim card options
  useEffect(() => {
    if (!openOptionsSimCards) setOptionsSimCards([]);
  }, [openOptionsSimCards]);
  useEffect(() => {

    let active = true;

    if (!loadingOptionsSimCards) return undefined;

    (async () => {

      try {

        // Get all sim cards
        const { data } = await api.get("simcards/view/options");

        if (data.status === "success") {
          if (active) setOptionsSimCards(data.result);
        } else {
          setOpenOptionsSimCards(false);
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      } catch (error) {

        setOpenOptionsSimCards(false);

        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      }
    })();

    return () => { active = false; };

  }, [loadingOptionsSimCards, addToast, t]);

  // Driver options
  useEffect(() => {
    if (!openOptionsDrivers) setOptionsDrivers([]);
  }, [openOptionsDrivers]);
  useEffect(() => {

    let active = true;

    if (!loadingOptionsDrivers) return undefined;

    (async () => {

      try {

        // Get all drivers
        const { data } = await api.get("drivers/view/options");

        if (data.status === "success") {
          if (active) setOptionsDrivers(data.result);
        } else {
          setOpenOptionsDrivers(false);
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      } catch (error) {
        setOpenOptionsDrivers(false);

        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      }
    })();

    return () => { active = false; };

  }, [loadingOptionsDrivers, addToast, t]);

  // Used to disable and change text when is searching for data
  useEffect(() => {

    const btnPesquisar = document.querySelector(".btn-pesquisar") as HTMLElement;

    if (btnPesquisar) {
      btnPesquisar.innerText = loadingHardwareList ? "Buscando hardwares..." : "Pesquisar";

      if (loadingHardwareList) {
        btnPesquisar.setAttribute("disabled", "");
      } else {
        btnPesquisar.removeAttribute("disabled");
      }
    }
  }, [loadingHardwareList]);

  // endregion Effects

  return (
    <>
      <Loading loading={loadingHardwareList} />
      {
        !loadingHardwareList && (
          <NewDataTable
            title={t(HardwareModuleMessages.hardwareDataTableTitle)}
            filters
            data={hardwares}
            columns={dataTableColumns}
            actions={dataTableActions}
            buttons={dataTableButtons}
            settings={dataTableSettings}
            returnTable={(table) => setTable(table)}
            returnClickedRow={(clickedRow) => setTableClickedRow(clickedRow)}
          />
        )
      }

      <DialogConfirmAction
        open={openDialogConfirmDelete}
        onClose={() => setOpenDialogConfirmDelete(false)}
        title={t(HardwareModuleMessages.hardwareDeleteTitle)}
        actions={[
          { text: t(GlobalMessages.no), action: () => setOpenDialogConfirmDelete(false) },
          { text: t(GlobalMessages.yes), action: () => deleteHardware() }
        ]}
      >
        {t(HardwareModuleMessages.hardwareDeleteText)} <br /><br />
        {t(HardwareModuleMessages.type)}: {hardwareToDelete?.type_description} <br />
        {t(HardwareModuleMessages.model)}: {hardwareToDelete?.model} <br />
        {t(HardwareModuleMessages.devId)}: {hardwareToDelete?.dev_id}?
      </DialogConfirmAction>
      <ContainerModalFormHardware id="modalFormHardware">
        <Dialog
          open={openModalFormHardware}
          onClose={() => setOpenModalFormHardware(false)}
          onExited={handleCloseModalForm}
          scroll="paper"
          container={document.getElementById("modalFormHardware")}
        >
          <DialogTitle className="mHeader">
            <div className="content">
              <div className="title">
                {
                  hardwareDetails
                    ? (`${hardwareDetails.type.description} – ${hardwareDetails.model}`)
                    : (t(HardwareModuleMessages.hardwareCreateTitle))
                }
              </div>
              <div className="subtitle">
                {
                  hardwareDetails
                    ? (
                      `${t(HardwareModuleMessages.devId)}: ${hardwareDetails?.dev_id}`
                    )
                    : ""
                }
              </div>
            </div>
            <div className="actions">
              {modalType === "details" && hardwareDetails &&
                (
                  <Button disableRipple onClick={() => setModalType("update")}><EditIcon /></Button>
                )}
            </div>
          </DialogTitle>
          <DialogContent dividers className="mContent">
            {loadingDetails ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress />
              </div>
            ) : (
              <Form className="form" ref={formRef} onSubmit={(formData) => validations.validateForm(formData, modalType)}>
                <DialogContentText tabIndex={-1} component="div">
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={12} lg={12} className="checkboxes">
                      <FormControlLabel
                        className="r-checkbox"
                        control={(
                          <Checkbox
                            onChange={(event) => setActiveCheckbox(event.target.checked)}
                            checked={activeCheckbox}
                            name="active"
                            disabled={modalType === "details"}
                          />
                        )}
                        label={t(HardwareModuleMessages.hardwareDetailsActive)}
                      />
                      <FormControlLabel
                        className="r-checkbox"
                        control={(
                          <Checkbox
                            onChange={(event) => setBlockCapableCheckbox(event.target.checked)}
                            checked={blockCapableCheckbox}
                            name="block_capable"
                            disabled={modalType === "details" || activeOptionTypeHardware?.description !== HardwareTypes.RASTREADOR}
                          />
                        )}
                        label={t(HardwareModuleMessages.doBlock)}
                      />
                      <FormControlLabel
                        className="r-checkbox"
                        control={(
                          <Checkbox
                            onChange={(event) => setWaterControlCapableCheckbox(event.target.checked)}
                            checked={waterControlCapableCheckbox}
                            name="water_control_capable"
                            disabled={modalType === "details" || activeOptionTypeHardware?.description !== HardwareTypes.RASTREADOR}
                          />
                        )}
                        label={t(HardwareModuleMessages.doWaterControl)}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <Autocomplete
                        open={openOptionsTypesHardware}
                        onOpen={() => setOpenOptionsTypesHardware(true)}
                        onClose={() => setOpenOptionsTypesHardware(false)}
                        getOptionSelected={
                          (option, value) => option?.id_hardware_type === value?.id_hardware_type
                        }
                        getOptionLabel={(option) => option?.description || ""}
                        options={
                          optionsTypesHardware.filter(utils.isNotNull)
                            .sort((a, b) => -b.description.localeCompare(a.description))
                        }
                        disabled={modalType === "details"}
                        loading={loadingOptionsTypesHardware}
                        onChange={(_, value) => {
                          setActiveOptionTypeHardware(value);
                          setActiveOptionVehicle(null);
                          setActiveOptionSimCard(null);
                          setActiveOptionDriver(null);
                          validations.validateFieldError("type");
                        }}
                        value={activeOptionTypeHardware}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="type"
                            label={t(HardwareModuleMessages.hardwareDetailsType)}
                            margin="dense"
                            variant="outlined"
                            helperText={t(GlobalMessages.required)}
                            InputLabelProps={{ shrink: !_.isEmpty(activeOptionTypeHardware) ? true : undefined }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingOptionsTypesHardware ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <Select
                        name="brand"
                        label={t(HardwareModuleMessages.brand)}
                        margin="dense"
                        variant="outlined"
                        disabled={modalType === "details"}
                        onChange={() => validations.validateFieldError("brand")}
                        defaultValue={
                          hardwareDetails?.brand ??
                          (
                            activeOptionTypeHardware?.id_hardware_type !== HardwareTypesID.IBUTTON
                              ? fixedValues.OtherTypes.brands[0] : fixedValues.Ibutton.brand
                          )
                        }
                      >
                        {activeOptionTypeHardware?.id_hardware_type !== HardwareTypesID.IBUTTON
                          ? fixedValues.OtherTypes.brands.map(
                            (brand) => <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                          )
                          : <MenuItem value={fixedValues.Ibutton.brand}>{fixedValues.Ibutton.brand}</MenuItem>}
                      </Select>
                      <FormHelperText>{t(GlobalMessages.required)}</FormHelperText>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <Select
                        name="model"
                        label={t(HardwareModuleMessages.model)}
                        margin="dense"
                        variant="outlined"
                        disabled={modalType === "details"}
                        onChange={() => validations.validateFieldError("model")}
                        defaultValue={
                          hardwareDetails?.model ??
                          (
                            activeOptionTypeHardware?.id_hardware_type !== HardwareTypesID.IBUTTON
                              ? fixedValues.OtherTypes.models[0] : fixedValues.Ibutton.model
                          )
                        }
                      >
                        {activeOptionTypeHardware?.id_hardware_type !== HardwareTypesID.IBUTTON
                          ? fixedValues.OtherTypes.models.map(
                            (model) => <MenuItem key={model} value={model}>{model}</MenuItem>
                          )
                          : <MenuItem value={fixedValues.Ibutton.model}>{fixedValues.Ibutton.model}</MenuItem>}
                      </Select>
                      <FormHelperText>{t(GlobalMessages.required)}</FormHelperText>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <TextField
                        label={t(HardwareModuleMessages.devId)}
                        margin="dense"
                        variant="outlined"
                        name="dev_id"
                        InputLabelProps={{ shrink: formRef.current?.getFieldValue("dev_id").length > 0 ? true : undefined }}
                        InputProps={{
                          endAdornment: (
                            <>{loadingValidateFieldDevId ? <CircularProgress color="inherit" size={20} /> : null}</>
                          )
                        }}
                        disabled={modalType === "details"}
                        helperText={t(GlobalMessages.required)}
                        defaultValue={hardwareDetails?.dev_id ?? ""}
                        onChange={() => validations.validateDevId(formRef.current?.getFieldValue("dev_id"))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <TextField
                        label={t(HardwareModuleMessages.waterCorrectionFactor)}
                        name="water_correction_factor"
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        helperText={t(HardwareModuleMessages.waterCorrectionFactorHelper)}
                        type="number"
                        inputProps={{step: 0.01, min: 0.5, max: 1.5}}
                        InputLabelProps={{ shrink: true }}
                        disabled={modalType === "details" || activeOptionTypeHardware?.id_hardware_type !== HardwareTypesID.RASTREADOR}
                        value={waterCorrectionFactor}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);

                          if (!isNaN(value)) setWaterCorrectionFactor(value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <TextField
                        label={t(HardwareModuleMessages.hardwareDetailsPurchaseDate)}
                        name="purchase_date"
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        helperText={t(GlobalMessages.required)}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        disabled={modalType === "details"}
                        onChange={() => validations.validateFieldError("purchase_date")}
                        defaultValue={
                          hardwareDetails?.purchase_date
                            ? dateFns.format(utils.getDateUTC(hardwareDetails.purchase_date), "yyyy-MM-dd")
                            : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <Autocomplete
                        open={openOptionsVehicles}
                        onOpen={() => setOpenOptionsVehicles(true)}
                        onClose={() => setOpenOptionsVehicles(false)}
                        getOptionSelected={(option, value) => option?.code === value?.code}
                        getOptionLabel={(option) =>
                          `${option?.code ? `${option?.code} - ` : ""}` +
                          `${option?.license_plate ? `${option?.license_plate} - ` : ""}` +
                          `${option?.description || ""}`
                        }
                        getOptionDisabled={(option) => option?.is_hardware_linked }
                        options={
                          optionsVehicles.sort((a, b) => -b?.type_description
                            .localeCompare(a?.type_description) || -b?.code.localeCompare(a?.code))
                        }
                        groupBy={(option) => option?.type_description}
                        disabled={modalType === "details"
                          || !activeOptionTypeHardware
                          || activeOptionTypeHardware.description === HardwareTypes.IBUTTON
                        }
                        loading={loadingOptionsVehicles}
                        onChange={(_, value) => setActiveOptionVehicle(value)}
                        value={activeOptionVehicle}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="vehicle"
                            label={t(HardwareModuleMessages.vehicle)}
                            variant="outlined"
                            margin="dense"
                            InputLabelProps={{ shrink: !_.isEmpty(activeOptionVehicle) ? true : undefined }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>{loadingOptionsVehicles ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>
                              )
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <Autocomplete
                        open={openOptionsSimCards}
                        onOpen={() => setOpenOptionsSimCards(true)}
                        onClose={() => setOpenOptionsSimCards(false)}
                        getOptionSelected={(option, value) => option?.caller_id === value?.caller_id}
                        getOptionLabel={(option) => `${option?.iccid ? `${option.iccid} - ` : ""}${option?.caller_id || ""}`}
                        getOptionDisabled={(option: any) => option?.is_hardware_linked}
                        options={optionsSimCards}
                        groupBy={(option) => option?.phone_company}
                        disabled={modalType === "details"
                          || !activeOptionTypeHardware
                          || activeOptionTypeHardware.description !== HardwareTypes.RASTREADOR
                        }
                        loading={loadingOptionsSimCards}
                        onChange={(_, value) => setActiveOptionSimCard(value)}
                        value={activeOptionSimCard}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="sim_card"
                            label={t(HardwareModuleMessages.simCard)}
                            margin="dense"
                            variant="outlined"
                            InputLabelProps={{ shrink: !_.isEmpty(activeOptionSimCard) ? true : undefined }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>{loadingOptionsSimCards ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>
                              )
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <Autocomplete
                        open={openOptionsDrivers}
                        onOpen={() => setOpenOptionsDrivers(true)}
                        onClose={() => setOpenOptionsDrivers(false)}
                        getOptionSelected={(option, value) => option?.name === value?.name}
                        getOptionLabel={(option) => option?.name || ""}
                        getOptionDisabled={(option) => !!option.is_hardware_linked}
                        options={optionsDrivers}
                        disabled={modalType === "details"
                          || !activeOptionTypeHardware
                          || activeOptionTypeHardware.description !== HardwareTypes.IBUTTON
                        }
                        loading={loadingOptionsDrivers}
                        onChange={(_, value) => setActiveOptionDriver(value)}
                        value={activeOptionDriver}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="driver"
                            label={t(HardwareModuleMessages.driver)}
                            margin="dense"
                            variant="outlined"
                            InputLabelProps={{ shrink: !!activeOptionDriver }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>{loadingOptionsDrivers ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>
                              )
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </DialogContentText>
              </Form>
            )}
          </DialogContent>
          <DialogActions className="mFooter">
            <div className="actions">
              {modalType === "update" && (
                <Button
                  disableRipple
                  type="submit"
                  color="primary"
                  onClick={() => formRef.current?.submitForm()}
                >{t(GlobalMessages.apply)}
                </Button>
              )}
              {modalType === "register" && (
                <Button
                  disableRipple
                  type="submit"
                  color="primary"
                  onClick={() => formRef.current?.submitForm()}
                >{t(GlobalMessages.register)}
                </Button>
              )}
            </div>
            <Button disableRipple onClick={() => setOpenModalFormHardware(false)} color="primary">{t(GlobalMessages.close)}</Button>
          </DialogActions>
        </Dialog>
      </ContainerModalFormHardware>
    </>
  );
};

export default HardwaresList;
